import type { WalkBoundsOptions } from '../_walk-bounds'
import type { TypeCallParams, TypeCallParamsV3, TypeFilterV3 } from '../../../types/http'
import { AbstractAction } from '../abstract-action'
import { SdkError } from '../../sdk-error'
import type { FilterV3Group } from '../../../tools/filter-v3'
import { assertTailFilter, filterMentionsField, keysetPaginate, KeysetPaginationError } from './_keyset-paginate'
import { CURSOR_STALLED_HINT_TAIL } from '../_cursor-stalled'
import { DESC_ORDER, resolveCursorDirection } from '../_cursor-progress'

export type ActionFetchTailV3 = WalkBoundsOptions & {
  method: string
  /**
   * `filter` is narrowed away from the `restApi:v2` object dialect, which the
   * portal rejects everywhere in v3. A **logic group** stays allowed: this
   * walker forwards `filter` untouched, and the portal accepts a bare group as
   * the whole filter — measured.
   */
  params?: Omit<TypeCallParamsV3, 'pagination' | 'order' | 'cursor' | 'filter'> & { filter?: TypeFilterV3 | FilterV3Group }
  cursorField?: string
  order?: 'ASC' | 'DESC' | 'asc' | 'desc' | string
  customKeyForResult?: string
  requestId?: string
  limit?: number
  initialValue?: number | string
}

/**
 * Calls a REST API `tail` method (native keyset cursor) and returns an async
 * generator for efficient large data retrieval. `restApi:v3`
 *
 * Unlike `fetchList`, which emulates keyset pagination on top of the `list`
 * action by injecting a `[field, '>', n]` filter, this helper drives the server
 * `tail` action with its native `cursor: { field, value, order, limit }`
 * parameter (see the v3 reference §6.2). The server itself adds `field > value`
 * (asc) / `field < value` (desc) and sorts by `field`, so the cursor field
 * MUST NOT appear in `filter` (the server rejects it with
 * `INVALIDFILTEREXCEPTION`).
 */
export class FetchTailV3 extends AbstractAction {
  /**
   * Streams every record of a `tail` method as chunks, advancing the keyset
   * cursor between requests.
   *
   * @template T - The type of items in the returned arrays (default is `unknown`).
   *
   * @param {ActionFetchTailV3} options - parameters for executing the request.
   *     - `method: string` - A REST API `tail` method name (for example: `main.eventlog.tail`).
   *     - `params?: Omit<TypeCallParamsV3, 'pagination' | 'order' | 'cursor'>` - Request parameters.
   *         Use `filter` and `select` to control the selection. `pagination`, `order` and `cursor`
   *         are managed by this helper and must not be passed. The cursor field must NOT be used in `filter`.
   *     - `cursorField?: string` - The DTO field that drives the cursor. Must be monotonic and
   *         preferably unique, and present in `select`. Default is `id`.
   *     - `order?: 'ASC' | 'DESC'` - Cursor direction. Default is `ASC`. For `DESC` you MUST pass
   *         `initialValue` (the server pages by `field < value`, so the default `0` returns nothing).
   *     - `customKeyForResult?: string` - The key the response groups rows under. Default is `items`.
   *     - `requestId?: string` - Unique request identifier for tracking.
   *     - `maxPages?: number` - Stop after this many pages and throw
   *         `JSSDK_ACTION_MAX_PAGES_EXCEEDED` naming the method. Defaults to 10 000 — a
   *         backstop, not a policy. Every page yielded before it fires has already
   *         been delivered to the consumer; only the walk stops.
   *     - `signal?: AbortSignal` - Stop the walk. Checked at the top of each iteration, so an
   *         already-aborted signal costs no request. Throws `JSSDK_ACTION_ABORTED`.
   *     - `limit?: number` - How many records to retrieve at a time. Default is `50`.
   *         **A request, not a guarantee.** Each method applies its own maximum and a page
   *         shorter than `limit` is not the end of the data — `tasks.task.list` answers 50
   *         however much you ask for, measured with 60 rows available. This walker is
   *         cap-tolerant; hand-rolled paging on `call.make` is not. On the build measured, a
   *         `limit` of `0` or a non-numeric one was refused with
   *         `INVALIDPAGINATIONEXCEPTION` and a negative one answered a bare 500 — one
   *         method on one on-premise build, so treat the codes as what to expect rather
   *         than a contract.
   *     - `initialValue?: number | string` - Cursor start value for the first page. Default is `0`
   *         (valid for ascending numeric fields); required for `DESC` and for non-numeric fields.
   *
   * @returns {AsyncGenerator<T[]>} An async generator that yields chunks of data as arrays of type `T`.
   *
   * @example
   * const generator = b24.actions.v3.fetchTail.make<{ id: string }>({
   *   method: 'main.eventlog.tail',
   *   params: { select: ['id', 'auditTypeId'] },
   *   cursorField: 'id',
   *   customKeyForResult: 'items'
   * })
   * for await (const chunk of generator) {
   *   console.log(`Processing ${chunk.length} items`)
   * }
   */
  public override async* make<T = unknown>(options: ActionFetchTailV3): AsyncGenerator<T[]> {
    const batchSize = options?.limit ?? 50
    const cursorField = options?.cursorField ?? 'id'
    const order = options?.order ?? 'ASC'
    const customKeyForResult = options?.customKeyForResult ?? 'items'
    const params = options?.params ?? {}

    assertTailFilter(params['filter'], 'fetchTail.make')

    // DESC keyset needs an explicit start: the server pages by `field < value`,
    // so the default first-page value 0 would match nothing for a non-negative
    // field. Require `initialValue` (the type maximum / newest value) for DESC.
    if (DESC_ORDER.test(order) && options?.initialValue === undefined) {
      throw new SdkError({
        code: 'JSSDK_CORE_B24_FETCH_TAIL_DESC_REQUIRES_INITIAL_VALUE',
        description: 'fetchTail.make: order "DESC" requires an explicit `initialValue` (the server pages by `field < value`, so the default 0 returns nothing). Pass `initialValue` set to the type maximum / newest value.',
        status: 500
      })
    }

    // The cursor field cannot also live in `filter` — the server forces ordering
    // and the `> value` condition on it and rejects a duplicate with
    // INVALIDFILTEREXCEPTION. Warn instead of letting the server 400. The scan
    // descends into logic groups: it used to look only at a top-level array of
    // triples, which stopped seeing anything at all once a bare group became a
    // legal filter here, and had never seen a group nested inside the array
    // form.
    if (filterMentionsField(params['filter'], cursorField)) {
      this._logger.warning(`fetchTail.make: the cursor field "${cursorField}" must not appear in \`filter\` — the server orders and pages by it and will reject a filter on the same field (INVALIDFILTEREXCEPTION). Remove it from \`filter\`.`).catch(() => {})
    }

    // The cursor field must be readable in the response to advance. Append it to
    // an explicit `select`; when `select` is omitted we rely on the server's
    // default field set — warn for a non-default cursorField, which may not be
    // in those defaults (the per-page guard below would otherwise stop silently).
    let select = params['select'] as string[] | undefined
    if (Array.isArray(select)) {
      if (!select.includes(cursorField)) {
        select = [...select, cursorField]
      }
    } else if (cursorField !== 'id') {
      this._logger.warning(`fetchTail.make: no \`select\` provided with a non-default cursorField "${cursorField}" — make sure it is in the server's default field set, otherwise pass \`select\` including "${cursorField}" so the cursor can advance.`).catch(() => {})
    }

    const { select: _ignoredSelect, ...restParams } = params as TypeCallParams

    try {
      yield* keysetPaginate<T>(this._b24, this._logger, {
        method: options.method,
        requestId: options.requestId,
        customKeyForResult,
        initialCursor: options?.initialValue ?? 0,
        // Native keyset: drive the server's `cursor: { field, value, order, limit }`.
        buildParams: cursor => ({
          ...restParams,
          ...(select ? { select } : {}),
          cursor: { field: cursorField, value: cursor, order, limit: batchSize }
        }),
        // Advance by the raw cursor-field value from the last item; a missing
        // value (cursorField not selected / wrong name) stops the walk.
        readNextCursor: lastItem => lastItem[cursorField] ?? null,
        noCursorWarning: `fetchTail.make: pagination stops here — no value could be read from the returned items via cursorField "${cursorField}". Make sure cursorField matches a field present in the response (and in \`select\`).`,
        errorLabel: 'fetchTailMethod',
        actionLabel: 'fetchTail.make',
        stalledCursorHint: CURSOR_STALLED_HINT_TAIL,
        // The caller's own `order`, normalised: the server pages by
        // `field > value` for ASC and `field < value` for DESC, so which way the
        // cursor must move is their choice rather than a constant here.
        cursorDirection: resolveCursorDirection(order),
        maxPages: options?.maxPages,
        signal: options?.signal
      })
    } catch (error) {
      if (error instanceof KeysetPaginationError) {
        throw new SdkError({
          code: 'JSSDK_CORE_B24_FETCH_TAIL_METHOD_API_V3',
          description: `API Error: ${error.messages.join('; ')}`,
          status: 500
        })
      }
      throw error
    }
  }
}
